import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 10 — TanStack Query hooks for Sessions, entitlement
 * and the TidyCal SchedulingAdapter. Mirrors practiceQueries.js's
 * `request()` error contract exactly (`.status`/`.body` on thrown
 * errors) so callers can branch on 402/403/404/409.
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });

export const sqk = {
  overview: (wrkId) => ["sessions-overview", wrkId],
  roomView: (wrkId, srpId) => ["session-room-view", wrkId, srpId],
  entitlement: (wrkId) => ["session-entitlement", wrkId],
  plans: (wrkId) => ["session-plans", wrkId],
  recaps: (wrkId, sesId) => ["session-recaps", wrkId, sesId],
};

export function useSessionsOverview(workspaceId) {
  return useQuery({
    queryKey: sqk.overview(workspaceId),
    enabled: !!workspaceId,
    retry: false,
    refetchInterval: 30000, // keeps the join-window/live state honest without a manual refresh
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/sessions`),
  });
}

export function useProposeSession(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/propose`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: sqk.overview(workspaceId) }),
  });
}

export function useAbandonProposal(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/propose/abandon`),
    onSuccess: () => qc.invalidateQueries({ queryKey: sqk.overview(workspaceId) }),
  });
}

export function useReconcileSessions(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (trigger) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/reconcile`, { trigger }),
    onSuccess: () => qc.invalidateQueries({ queryKey: sqk.overview(workspaceId) }),
  });
}

export function useCancelSession(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sesId, reason }) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/cancel`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: sqk.overview(workspaceId) }),
  });
}

export function useMarkNoShow(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sesId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/no-show`),
    onSuccess: () => qc.invalidateQueries({ queryKey: sqk.overview(workspaceId) }),
  });
}

export function useAddRecap(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sesId, summaryText }) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/recap`, { summary_text: summaryText }),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: sqk.recaps(workspaceId, vars.sesId) }),
  });
}

export function useSessionRecaps(workspaceId, sesId) {
  return useQuery({
    queryKey: sqk.recaps(workspaceId, sesId),
    enabled: !!workspaceId && !!sesId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/recaps`),
  });
}

export function useEntitlement(workspaceId) {
  return useQuery({
    queryKey: sqk.entitlement(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/entitlement`),
  });
}

export function useGrantEntitlement(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/entitlement/grant`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sqk.entitlement(workspaceId) });
      qc.invalidateQueries({ queryKey: sqk.overview(workspaceId) });
    },
  });
}

export function useAddSessionCredits(workspaceId) {
  // Master v5 Prompt 30G — Client 360's "+ Add sessions". Adds
  // entitlement/credits ONLY (never a booked Session), so every Client
  // 360 tab that shows a balance (this member's, and any co-participant's
  // for a group grant) must be invalidated by prefix.
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/entitlement/grants`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-record"] });
      qc.invalidateQueries({ queryKey: sqk.entitlement(workspaceId) });
      qc.invalidateQueries({ queryKey: sqk.overview(workspaceId) });
    },
  });
}

export function useSessionPlans(workspaceId) {
  return useQuery({
    queryKey: sqk.plans(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/session-plans`),
  });
}

export function useCreateSessionPlan(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/session-plans`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: sqk.plans(workspaceId) }),
  });
}

export function useCreateSessionRoomLaunch(workspaceId) {
  return useMutation({
    mutationFn: (sesId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/room`),
  });
}

// Cross-workspace variants — CoachSessionsPage's Day/Week/Agenda spans
// EVERY Workspace at once, so the workspace_id is only known per-row at
// call time, never fixed at hook-creation time like the bound hooks above.
export const createSessionRoomLaunch = (workspaceId, sesId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/room`);
export const cancelSessionDirect = (workspaceId, sesId, reason) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/cancel`, { reason });
export const markNoShowDirect = (workspaceId, sesId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/no-show`);

export function useSessionRoomViewByPlan(workspaceId, srpId) {
  return useQuery({
    queryKey: sqk.roomView(workspaceId, srpId),
    enabled: !!workspaceId && !!srpId,
    retry: false,
    refetchInterval: 30000,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/session-room-plans/${srpId}/view`),
  });
}

// ---------------------------------------------------------------------------
// Master v5 Prompt 18 — SessionTimeProposal (propose-a-time / reschedule)
// and Complete Session.
// ---------------------------------------------------------------------------
export function useTimeProposals(workspaceId) {
  return useQuery({
    queryKey: ["time-proposals", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/time-proposals`),
  });
}

export function useCreateTimeProposal(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/time-proposals`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["time-proposals", workspaceId] });
      qc.invalidateQueries({ queryKey: sqk.overview(workspaceId) });
      qc.invalidateQueries({ queryKey: ["coach-sessions"] });
    },
  });
}

export function useAcceptTimeProposal(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stpId, slotId }) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/time-proposals/${stpId}/accept`, { slot_id: slotId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["time-proposals", workspaceId] });
      qc.invalidateQueries({ queryKey: sqk.overview(workspaceId) });
    },
  });
}

export function useDeclineTimeProposal(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stpId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/time-proposals/${stpId}/decline`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["time-proposals", workspaceId] });
      qc.invalidateQueries({ queryKey: sqk.overview(workspaceId) });
    },
  });
}

export function useCompleteSession(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sesId, payload }) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/complete`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sqk.overview(workspaceId) });
      qc.invalidateQueries({ queryKey: ["coach-sessions"] });
    },
  });
}

export function useSessionCompletion(workspaceId, sesId) {
  return useQuery({
    queryKey: ["session-completion", workspaceId, sesId],
    enabled: !!workspaceId && !!sesId,
    retry: false,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/completion`),
  });
}

// Coach-wide Day/Week/Agenda — cross-Workspace, never scoped to a single
// wrk_id (see routers/session_routes.py's /coach/sessions).
export function useCoachSessions({ rangeStart, rangeEnd, workspaceId }) {
  return useQuery({
    queryKey: ["coach-sessions", rangeStart, rangeEnd, workspaceId || "all"],
    enabled: !!rangeStart && !!rangeEnd,
    queryFn: () => {
      const params = new URLSearchParams({ range_start: rangeStart, range_end: rangeEnd });
      if (workspaceId) params.set("workspace_id", workspaceId);
      return request(`/api/rehearsal-room/coach/sessions?${params.toString()}`);
    },
  });
}

// Master v5 Prompt 32 — Client 360 -> Sessions -> "Book Session" / "Start
// Session Now". Real TidyCal slots (read-only, always works); the two
// mutations are coach-direct, no proposal/acceptance step.
export function useAvailableSlots(workspaceId, { startsAt, endsAt, enabled = true } = {}) {
  return useQuery({
    queryKey: ["available-slots", workspaceId, startsAt, endsAt],
    enabled: !!workspaceId && !!startsAt && !!endsAt && enabled,
    retry: false,
    queryFn: () => {
      const params = new URLSearchParams({ starts_at: startsAt, ends_at: endsAt });
      return request(`/api/rehearsal-room/workspaces/${workspaceId}/scheduling/available-slots?${params.toString()}`);
    },
  });
}

function _invalidateAfterDirectBooking(qc, workspaceId, memberId) {
  qc.invalidateQueries({ queryKey: ["client-record", "sessions", workspaceId, memberId] });
  qc.invalidateQueries({ queryKey: sqk.overview(workspaceId) });
  qc.invalidateQueries({ queryKey: ["coach-sessions"] });
}

export function useBookSessionDirect(workspaceId, memberId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/clients/${memberId}/sessions/book`, payload),
    onSuccess: () => _invalidateAfterDirectBooking(qc, workspaceId, memberId),
  });
}

export function useStartSessionNow(workspaceId, memberId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/clients/${memberId}/sessions/start-now`),
    onSuccess: () => _invalidateAfterDirectBooking(qc, workspaceId, memberId),
  });
}
